import gsap from "gsap";

/**
 * Page Transition Animation Handler
 * Creates smooth stroke animations for SVG paths
 */
export class PageTransitionAnimation {
  private pathsRef: SVGPathElement[] = [];
  private timeline: gsap.core.Timeline | null = null;

  /**
   * Initialize paths from SVG element
   * @param containerSelector - CSS selector for the container with SVG
   */
  initializePaths(containerSelector: string = "#page-transition"): void {
    const pageTransition = document.querySelector(containerSelector);
    if (!pageTransition) {
      console.warn(`Container "${containerSelector}" not found`);
      return;
    }

    this.pathsRef = Array.from(pageTransition.querySelectorAll("path"));
    this.setupPathStyles();
  }

  /**
   * Setup initial stroke dash styles for paths
   */
  private setupPathStyles(): void {
    this.pathsRef.forEach((path) => {
      const length = path.getTotalLength();
      path.style.strokeDasharray = `${length}`;
      path.style.strokeDashoffset = `${length}`;
    });
  }

  /**
   * Animate paths on page leave (stroke reveal animation)
   * @param duration - Animation duration in seconds
   * @param ease - GSAP easing function
   * @returns Promise that resolves when animation completes
   */
  animateLeave(
    duration: number = 1,
    ease: string = "power1.inOut"
  ): Promise<void> {
    return new Promise((resolve) => {
      this.timeline = gsap.timeline({ onComplete: resolve });

      this.pathsRef.forEach((path) => {
        this.timeline!.to(
          path,
          {
            strokeDashoffset: 0,
            attr: {
              "stroke-width": 700,
            },
            duration,
            ease,
          },
          0
        );
      });
    });
  }

  /**
   * Animate paths on page enter (stroke hide animation)
   * @param duration - Animation duration in seconds
   * @param ease - GSAP easing function
   * @returns Promise that resolves when animation completes
   */
  animateEnter(
    duration: number = 1,
    ease: string = "power1.inOut"
  ): Promise<void> {
    return new Promise((resolve) => {
      this.timeline = gsap.timeline({ onComplete: resolve });

      this.pathsRef.forEach((path) => {
        const length = path.getTotalLength();
        this.timeline!.to(
          path,
          {
            strokeDashoffset: -length,
            attr: { "stroke-width": 200 },
            duration,
            ease,
            onComplete: () => {
              gsap.set(path, {
                strokeDashoffset: length,
              });
            },
          },
          0
        );
      });
    });
  }

  /**
   * Kill current animation timeline
   */
  kill(): void {
    if (this.timeline) {
      this.timeline.kill();
      this.timeline = null;
    }
  }

  /**
   * Reset all paths to initial state
   */
  reset(): void {
    this.kill();
    this.pathsRef.forEach((path) => {
      const length = path.getTotalLength();
      gsap.set(path, {
        strokeDashoffset: length,
        attr: { "stroke-width": 200 },
      });
    });
  }
}

/**
 * Create and return a new PageTransitionAnimation instance
 */
export function createPageTransition(): PageTransitionAnimation {
  return new PageTransitionAnimation();
}

/**
 * Predefined animation presets
 */
export const AnimationPresets = {
  FAST: { duration: 0.6, ease: "power1.inOut" },
  NORMAL: { duration: 1, ease: "power1.inOut" },
  SLOW: { duration: 1.4, ease: "power1.inOut" },
  SMOOTH: { duration: 1, ease: "sine.inOut" },
  BOUNCE: { duration: 1, ease: "back.out" },
};

/**
 * Global animation instance singleton
 */
let globalInstance: PageTransitionAnimation | null = null;

/**
 * Get or create global animation instance
 */
export function getGlobalTransition(): PageTransitionAnimation {
  if (!globalInstance) {
    globalInstance = new PageTransitionAnimation();
  }
  return globalInstance;
}

/**
 * Initialize global animation instance
 */
export function initializeGlobalTransition(
  containerSelector: string = "#page-transition"
): void {
  const instance = getGlobalTransition();
  instance.initializePaths(containerSelector);
}
